import { useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import type { Chat } from '@trinity/chat';
import {
  AttachmentsContext,
  DisplayModeContext,
  ToggleDisplayModeContext,
} from '@trinity/chat-components/components/Chat/context';
import { EvidencePopover } from '@trinity/chat-components/components/presentation/EvidencePopover';
import { MetricsPopover } from '@trinity/chat-components/components/presentation/MetricsPopover';

// ─── Types ──────────────────────────────────────────────────────────────────

type GeneratedMessageSummary = Chat.Attachment.GeneratedMessageSummary;

// ─── Helpers ────────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 5;

function normalizeCategory(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Main Component ─────────────────────────────────────────────────────────

interface GeneratedMessagesTableProps {
  id: string;
}

export function GeneratedMessagesTable({ id }: GeneratedMessagesTableProps): React.JSX.Element | null {
  const attachments = useContext(AttachmentsContext);
  const displayMode = useContext(DisplayModeContext);
  const toggleDisplayMode = useContext(ToggleDisplayModeContext);
  const attachment = useMemo(
    () => attachments.find((x) => x.id === id) as Chat.Attachment.GeneratedMessages | undefined,
    [attachments, id]
  );

  const [page, setPage] = useState(0);

  if (!attachment || displayMode === 'compact') return null;

  const { phase, messages } = attachment.data;
  const totalPages = Math.ceil(messages.length / ROWS_PER_PAGE);
  const pageMessages = messages.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  const showRank = messages.every((m) => m.rank !== null && m.rank !== undefined);

  let phaseLabel: string;
  if (phase === 'generated') {
    phaseLabel = 'Generated';
  } else if (phase === 'prioritized') {
    phaseLabel = 'Prioritized';
  } else {
    phaseLabel = 'Curated';
  }

  return (
    <Root>
      <Header>
        <HeaderTitle>
          {phaseLabel} Messages
        </HeaderTitle>
        <HeaderCount>{messages.length} messages</HeaderCount>
      </Header>
      <TableScroller>
        <StyledTable>
          <thead>
            <tr>
              <ThId>ID</ThId>
              {showRank && <ThId>Rank</ThId>}
              <ThMessage>Message</ThMessage>
              <ThCategory>Category</ThCategory>
              <ThAction>Metrics</ThAction>
              <ThAction>Evidence</ThAction>
            </tr>
          </thead>
          <tbody>
            {pageMessages.map((msg) => (
              <MessageRow key={msg.identifier} msg={msg} showRank={showRank} />
            ))}
          </tbody>
        </StyledTable>
      </TableScroller>
      {totalPages > 1 && (
        <PaginationBar>
          <PaginationButton disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PaginationButton>
          <PaginationInfo>
            {page + 1} / {totalPages}
          </PaginationInfo>
          <PaginationButton disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </PaginationButton>
        </PaginationBar>
      )}
      <HintText>
        View full message details by{' '}
        {toggleDisplayMode ? (
          <HintLink onClick={toggleDisplayMode}>expanding the messages pane</HintLink>
        ) : (
          <HintLink
            href={`/message-generator/${attachment.data.messageGenSessionId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            opening the message generator
          </HintLink>
        )}
        .
      </HintText>
    </Root>
  );
}

// ─── Row Component ──────────────────────────────────────────────────────────

function MessageRow({
  msg,
  showRank,
}: {
  msg: GeneratedMessageSummary;
  showRank: boolean;
}): React.JSX.Element {
  const hasMetrics = msg.metrics !== null && msg.metrics !== undefined;

  return (
    <tr>
      <TdId>{msg.code}</TdId>
      {showRank && <TdRank>{msg.rank}</TdRank>}
      <TdMessage>
        <Tooltip title={msg.body} placement="right" enterDelay={300}>
          <MessageText>{msg.headline}</MessageText>
        </Tooltip>
      </TdMessage>
      <TdCategory>{normalizeCategory(msg.category)}</TdCategory>
      <TdAction>
        <div>{hasMetrics && <MetricsPopover metrics={msg.metrics} />}</div>
      </TdAction>
      <TdAction>
        <div>
          <EvidencePopover trialAnchors={msg.trialAnchors} voiceAnchors={msg.voiceAnchors} />
        </div>
      </TdAction>
    </tr>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  max-height: 500px;
  border: 1px solid var(--gray-l);
  border-radius: 5px;
`;

const Header = styled.div`
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
`;

const HeaderCount = styled.span`
  font-size: 12px;
  color: #666;
`;

const TableScroller = styled.div`
  flex: 1;
  overflow: auto;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  th,
  td {
    padding: 0;
  }

  thead tr {
    background: #f8f9fa;
    border-bottom: 2px solid #e0e0e0;
  }

  tbody td {
    border-bottom: 1px solid #e8e8e8;
  }

  tbody tr:hover {
    background: #f9f9f9;
  }
`;

const ThId = styled.th`
  text-align: center;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  color: #1a1a1a;
  width: 40px;
`;

const ThMessage = styled.th`
  text-align: left;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  color: #1a1a1a;
  min-width: 180px;
`;

const ThCategory = styled.th`
  text-align: left;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  color: #1a1a1a;
  min-width: 100px;
`;

const ThAction = styled.th`
  text-align: center;
  padding: 0 4px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  color: #1a1a1a;
  width: 60px;
`;

const TdId = styled.td`
  text-align: center;
  padding: 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(18, 113, 196);
`;

const TdRank = styled.td`
  text-align: center;
  padding: 8px;
  font-size: 11px;
  color: #888;
`;

const TdMessage = styled.td`
  padding: 8px;
  min-width: 180px;
`;

const MessageText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  cursor: default;
  display: block;
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
`;

const TdCategory = styled.td`
  padding: 8px;
  font-size: 12px;
  color: #555;
`;

const TdAction = styled.td`
  padding: 4px;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// ─── Pagination ─────────────────────────────────────────────────────────────

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const PaginationButton = styled.button`
  font-size: 11px;
  font-weight: 500;
  color: #555;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover:not(:disabled) {
    color: #1271c4;
    background: #e8f0fe;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const PaginationInfo = styled.span`
  font-size: 11px;
  color: #888;
`;

const HintText = styled.p`
  margin: 8px 16px 4px;
  font-size: 11px;
  color: #888;
`;

const HintLink = styled('a')`
  color: rgb(18, 113, 196);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0d5a9e;
  }
`;

GeneratedMessagesTable.displayName = 'Chat.Attachments.GeneratedMessagesTable';
